import { z } from 'zod';

import { Tooltip } from '../tooltip/Tooltip';

import styles from './progressBar.module.scss';

const progressBarSegmentSchema = z.object({
  value: z.number(),
  valueFormatted: z.string().optional(),
  color: z.string().optional(),
  label: z.string(),
});

const progressBarSchema = z.object({
  segments: z.array(progressBarSegmentSchema),
  unit: z.string().optional(),
  label: z.string().optional(),
});

type ProgressBarSegment = z.infer<typeof progressBarSegmentSchema>;
export type ProgressBarProps = z.infer<typeof progressBarSchema>;

export const areValidProgressBarProps = (v: unknown): v is ProgressBarProps => {
  return progressBarSchema.safeParse(v).success;
};

// define default color scales for segments
const SEGMENT_GREY_SCALE = [
  'rgb(71, 72, 73)',
  'rgb(120, 123, 124)',
  'rgb(173, 177, 180)',
];

function segmentDefaultColor(idx: number) {
  // use colored scale for first three segments
  if (idx <= 2) {
    return `rgba(var(--portal-ui-color-progressbar-${idx + 1}))`;
  }
  // use grey scale for all further segments
  return SEGMENT_GREY_SCALE[idx % 3];
}

type TooltipRowProps = {
  segment: ProgressBarSegment;
  segmentIndex: number;
  unit?: string;
};

const TooltipRow = ({
  segment: { color, label, value, valueFormatted },
  segmentIndex,
  unit,
}: TooltipRowProps) => {
  return (
    <div className={styles['tooltip-row']}>
      <div className={styles['tooltip-label-container']}>
        <div
          className={styles['tooltip-label-dot']}
          style={{
            background: color ?? segmentDefaultColor(segmentIndex),
          }}
        ></div>
        <div>{label}</div>
      </div>
      <div className={styles['tooltip-value']}>
        <div>
          {valueFormatted != null ? valueFormatted : value + (unit ?? '')}
        </div>
      </div>
    </div>
  );
};

export const ProgressBar = ({ segments, unit, label }: ProgressBarProps) => {
  return (
    <Tooltip
      label={
        <>
          {segments.map((segment, idx) => (
            <TooltipRow
              key={idx}
              unit={unit}
              segment={segment}
              segmentIndex={idx}
            />
          ))}
        </>
      }
      position="left"
    >
      <div className={styles['container']}>
        {label && <div className={styles['label']}>{label}</div>}
        <div className={styles['bar']}>
          {segments.map((segment, idx) => (
            <div
              className={styles['segment']}
              style={{
                backgroundColor: segment.color ?? segmentDefaultColor(idx),
                flex: segment.value, // flex: 100 1 0px;
              }}
              key={idx}
            >
              <div>
                {segment.valueFormatted != null
                  ? segment.valueFormatted
                  : segment.value + (unit ?? '')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Tooltip>
  );
};
